import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Users, CalendarDays, Newspaper, Wallet, Mail, HeartHandshake, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/api";
import { greetingFor, formatDate } from "@/constants";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n ?? 0);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [audience, setAudience] = useState(null);
  const outlet = useOutletContext();
  const user = outlet?.user;

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/audience?days=14").then((r) => setAudience(r.data)).catch(() => setAudience([]));
  }, []);

  const cards = [
    { label: "Adhérents actifs", value: stats?.members, icon: Users, to: "/admin/membres", id: "members" },
    { label: "Bénévoles actifs", value: stats?.volunteers, icon: HeartHandshake, to: "/admin/benevoles", id: "volunteers" },
    { label: "Événements à venir", value: stats?.events_upcoming, icon: CalendarDays, to: "/admin/contenus", id: "events" },
    { label: "Actualités publiées", value: stats?.news, icon: Newspaper, to: "/admin/contenus", id: "news" },
    { label: "Messages non lus", value: stats?.unread_messages, icon: Mail, to: "/admin/messages", id: "messages" },
  ];

  const maxViews = audience && audience.length ? Math.max(1, ...audience.map((d) => d.views)) : 1;
  const todayRow = audience && audience.length ? audience[audience.length - 1] : null;
  const totalViews = audience ? audience.reduce((s, d) => s + d.views, 0) : 0;
  const totalVisitors = audience ? audience.reduce((s, d) => s + d.visitors, 0) : 0;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-dashboard-title">Vue d'ensemble</h1>
        <p className="text-sm text-slate-500 mt-1" data-testid="admin-greeting">{greetingFor(user)} — pilotage de l'association au quotidien.</p>
      </div>

      <Card className="border-emerald-200 bg-emerald-50" data-testid="admin-balance-card">
        <CardContent className="p-6 flex items-center gap-5">
          <div className="h-12 w-12 rounded-2xl bg-emerald-600 flex items-center justify-center shrink-0">
            <Wallet className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-emerald-800">Solde de trésorerie</p>
            <p className="font-heading text-3xl font-extrabold text-emerald-900" data-testid="admin-balance-value">
              {stats ? (stats.treasury_balance == null ? "—" : fmt(stats.treasury_balance)) : "…"}
            </p>
          </div>
          <Link to="/admin/tresorerie" className="ml-auto text-sm font-medium text-emerald-800 hover:underline" data-testid="admin-balance-link">
            Gérer la trésorerie →
          </Link>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map(({ label, value, icon: Icon, to, id }) => (
          <Link key={id} to={to} data-testid={`admin-stat-${id}`}>
            <Card className="border-slate-200 hover:shadow-md transition-shadow h-full">
              <CardContent className="p-5 space-y-3">
                <Icon className="h-5 w-5 text-sky-700" />
                <p className="font-heading text-2xl font-bold text-slate-900">{value ?? "…"}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {audience && audience.length > 0 && (
        <Card className="border-slate-200" data-testid="admin-audience-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-1">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-slate-900">Audience du site</h2>
            </div>
            <p className="text-xs text-slate-400 mb-5">Visites et visiteurs uniques au jour le jour — mesure anonyme, sans cookie.</p>
            <div className="flex flex-wrap gap-8 mb-6">
              <div>
                <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="audience-today-views">{todayRow?.views ?? 0}</p>
                <p className="text-xs text-slate-500">Visites aujourd'hui</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="audience-today-visitors">{todayRow?.visitors ?? 0}</p>
                <p className="text-xs text-slate-500">Visiteurs uniques aujourd'hui</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="audience-total-views">{totalViews}</p>
                <p className="text-xs text-slate-500">Visites sur {audience.length} j</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="audience-total-visitors">{totalVisitors}</p>
                <p className="text-xs text-slate-500">Visiteurs uniques sur {audience.length} j</p>
              </div>
            </div>
            <div className="flex items-end gap-1.5" data-testid="audience-chart">
              {audience.map((d) => (
                <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-emerald-500/85 rounded-t-md hover:bg-emerald-600 transition-colors"
                    style={{ height: `${Math.max(4, Math.round((d.views / maxViews) * 96))}px` }}
                    title={`${formatDate(d.date)} : ${d.views} visite(s) · ${d.visitors} visiteur(s) unique(s)`}
                    data-testid={`audience-bar-${d.date}`}
                  />
                  <span className="text-[9px] text-slate-400">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
